/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CarouselSlider } from '@/components/blocks/Carousel';
import { RenderContent } from '@/components/blocks/RenderContent';
import {
	contentCarouselContainerStylesSx,
	contentCarouselSkeletonSx,
} from '@/components/content/ContentCarousel/styles/contentCarouselStyles';
import { useContentCarousel } from '@/data/Content/ContentCarousel';
import { useContentEvents } from '@/data/Content/_ContentEvents';
import { CarouselOptions } from '@/data/types/Carousel';
import { WidgetComponentProps, WidgetProperties } from '@/data/types/Slot';
import { getCarouselProperties } from '@/utils/getCarouselProperties';
import { renderContent } from '@/utils/renderContent';
import { Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import { FC, useMemo, useState } from 'react';

const defaultProps: CarouselOptions = {
	naturalSlideWidth: 600,
	naturalSlideHeight: 400,
	visibleSlides: 1,
};

const emptyProperties = {} as WidgetProperties;

type ContentCarouselProps = WidgetComponentProps & {
	/**
	 * Do not apply any replacements to the content (See `parseHTMLValidate`).
	 */
	skipParsing?: boolean;
};

export const ContentCarousel: FC<ContentCarouselProps> = ({
	id: _id,
	properties = emptyProperties,
	skipParsing,
}) => {
	const { emsName = '' } = properties;
	const { data, title, loading } = useContentCarousel(emsName);
	const [hovering, setHovering] = useState<boolean>(false);
	const { breakpoints } = useTheme();
	const isDesktop = useMediaQuery(breakpoints.up('md'));
	const { onContentClick } = useContentEvents();
	const showPaginationCustomComponent = isDesktop ? hovering : true;

	const carouselProps = useMemo(
		() => getCarouselProperties(properties, defaultProps),
		[properties]
	);

	const slides = useMemo(
		() =>
			(data || [])
				.map((c) =>
					renderContent(c, onContentClick(c), {
						skipParsing,
					})
				)
				.filter(Boolean),
		[data, onContentClick, skipParsing]
	) as JSX.Element[];

	const interval = carouselProps?.interval;

	const paginationDotsColor = {
		['--dot-color' as string]: carouselProps.isPlaying ? '#CCCCCC' : '#333333',
		['--dot-fill-color' as string]: '#333333',
		['--dot-interval' as string]: carouselProps.isPlaying ? `${interval}ms` : '',
	} as React.CSSProperties;

	if (loading) {
		return <Skeleton variant="rounded" sx={contentCarouselSkeletonSx} />;
	}

	if (!slides.length) return null;

	return (
		<Box sx={contentCarouselContainerStylesSx}>
			{title?.map((content) => (
				<RenderContent
					key={`${content.id}${content.contentId}`}
					content={content}
					onClick={onContentClick(content)}
					skipParsing={skipParsing}
				/>
			))}
			<Box onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
				<CarouselSlider
					customPaginationStyles={paginationDotsColor}
					showPaginationCustomComponent={showPaginationCustomComponent}
					showNavigationCustom={isDesktop ? hovering : false}
					slides={slides}
					carouselProps={carouselProps}
				/>
			</Box>
		</Box>
	);
};
